import useSWR from 'swr'

import { options, propertyApi, tokenOptions } from '../../apis/suitebookClient'

export type UsePlanWithRoomsApiProps = {
  propertySlug: string
  planSlug: string
  checkInDate?: string
  checkOutDate?: string
  roomCount?: number
  numberOfAdults?: number
  numberOfChildren?: number
  numberOfInfants?: number
  // Room-list controls, applied server-side (parity with `useRoomsApi`):
  // `showAllRooms=false` returns only reservable rooms; `order` sorts by
  // price; `roomSlug` scopes the list to a single room.
  showAllRooms?: boolean
  order?: 'min_price' | '-min_price'
  roomSlug?: string
  accessToken?: string
}

/**
 * Fetches a plan (indexed by plan slug only) together with the rooms it is
 * bookable on. Backend returns plan detail with `availableRooms[].plan = null`
 * when search params are incomplete; partial subsets are tolerated and yield
 * the same plan-only response (no 400).
 *
 * See docs/specs/2026-05-28-secret-plan-design.md §5.2.
 */
export const usePlanWithRoomsApi = ({
  propertySlug,
  planSlug,
  checkInDate,
  checkOutDate,
  roomCount,
  numberOfAdults,
  numberOfChildren,
  numberOfInfants,
  showAllRooms,
  order,
  roomSlug,
  accessToken,
}: UsePlanWithRoomsApiProps) => {
  const { data, isLoading, isValidating, error, mutate } = useSWR(
    // Gate on the required slugs so an empty `propertySlug` / `planSlug`
    // (e.g. when `useParams()` returned undefined and the caller coerced to
    // '') doesn't fire a request to `/hotels//plans/`. Mirrors `usePlanApi`.
    propertySlug && planSlug
      ? ([
          'plan-with-rooms',
          propertySlug,
          planSlug,
          checkInDate ?? null,
          checkOutDate ?? null,
          roomCount ?? null,
          numberOfAdults ?? null,
          numberOfChildren ?? null,
          numberOfInfants ?? null,
          showAllRooms ?? null,
          order ?? null,
          roomSlug ?? null,
        ] as const)
      : null,
    propertySlug && planSlug
      ? () =>
          propertyApi
            .getPlanWithRooms(
              propertySlug,
              planSlug,
              checkInDate,
              checkOutDate,
              roomCount,
              numberOfAdults,
              numberOfChildren,
              numberOfInfants,
              showAllRooms,
              order,
              roomSlug,
              accessToken ? tokenOptions(accessToken) : options
            )
            .then((res) => res.data)
      : () => undefined,
    {
      revalidateOnFocus: false,
    }
  )

  return { data, isLoading, isValidating, error, mutate }
}
